/**
 * Runs the commands an admin issues from Device Management (DPDP Phase 9 -
 * until now the server queued them and nothing on the device ever asked).
 *
 * Polled with every heartbeat (lib/deviceHeartbeat.ts) on a paired kiosk.
 * Each command is acknowledged PROCESSING, then COMPLETED or FAILED; the
 * server audits the outcome. Unknown types fail rather than being ignored,
 * so the console shows a newer command an older build cannot run.
 *
 * A decommissioned device is refused everything by the server
 * (DEVICE_DECOMMISSIONED) - including fetching a wipe command - so hearing
 * that is itself the instruction to erase.
 *
 * Either way it first sends its unsent bills (lib/finalSync.ts) and erases
 * only once none is left; otherwise it tries again on the next heartbeat.
 */
import { LATEST_SCHEMA_VERSION } from '@kortex/pos-local';
import {
  acknowledgeDeviceCommand,
  DeviceApiError,
  deviceCredentials,
  fetchDeviceCommands,
  type DeviceCredentials,
  type PendingDeviceCommand,
} from './deviceApi';
import { persistedDeviceId } from './deviceId';
import { sendUnsentWork } from './finalSync';
import { wipeThisDevice } from './localWipe';
import { getActivePosDataSource } from './posData';

let running = false;

export async function runPendingDeviceCommands(): Promise<void> {
  if (running) return;
  const credentials = deviceCredentials(persistedDeviceId());
  if (!credentials) return; // not paired: no commands can exist for it
  running = true;
  try {
    const commands = await fetchDeviceCommands(credentials);
    for (const command of commands) await runOne(credentials, command);
  } catch (error) {
    await handleRefusal(error);
  } finally {
    running = false;
  }
}

/**
 * The server's answer to a device-authenticated call, acted on: a
 * decommissioned device erases itself and returns to the pairing screen.
 * Exported for the heartbeat, which hears it first.
 */
export async function handleRefusal(error: unknown): Promise<boolean> {
  if (error instanceof DeviceApiError && error.code === 'DEVICE_DECOMMISSIONED') {
    const credentials = deviceCredentials(persistedDeviceId());
    const outcome = credentials ? await sendUnsentWork(credentials).catch(() => null) : { unsent: 0, rejected: 0 };
    if (!outcome || outcome.unsent > 0) {
      // Bills whose only copy is here: keep them, and try again next heartbeat.
      console.warn('[device] decommissioned; erasing once unsent bills have been sent');
      return true;
    }
    await wipeThisDevice();
    window.location.replace('/');
    return true;
  }
  if (!(error instanceof DeviceApiError && error.code === 'NOT_PAIRED')) {
    console.warn('[device] command poll failed', error);
  }
  return false;
}

async function runOne(credentials: DeviceCredentials, command: PendingDeviceCommand): Promise<void> {
  const ack = (outcome: Parameters<typeof acknowledgeDeviceCommand>[2]) =>
    acknowledgeDeviceCommand(credentials, command.id, outcome).catch((error: unknown) => {
      console.warn('[device] could not acknowledge a command', error);
    });

  await ack({ status: 'PROCESSING' });
  try {
    switch (command.type) {
      case 'SYNC_NOW':
      case 'REFRESH_CONFIGURATION':
        // A pull is how configuration reaches the till.
        await getActivePosDataSource()?.syncNow();
        await ack({ status: 'COMPLETED' });
        return;
      case 'RUN_DIAGNOSTIC': {
        const diagnostics = await getActivePosDataSource()?.getSyncDiagnostics().catch(() => null);
        // Counts and states only - nothing personal leaves in a diagnostic.
        await ack({
          status: 'COMPLETED',
          result: {
            schemaVersion: LATEST_SCHEMA_VERSION,
            online: navigator.onLine,
            syncStatus: diagnostics?.status ?? null,
            pending: diagnostics?.pending ?? null,
            failed: diagnostics?.failed ?? null,
            openConflicts: diagnostics?.openConflicts ?? null,
          },
        });
        return;
      }
      case 'DISABLE_DEVICE':
      case 'ENABLE_DEVICE':
        // Enforced by the server (deviceAuth); nothing to do on the device.
        await ack({ status: 'COMPLETED' });
        return;
      case 'WIPE_LOCAL_DATA': {
        const sent = await sendUnsentWork(credentials);
        if (sent.unsent > 0) {
          // Left PROCESSING, so the server hands it back on the next poll and
          // this runs again - until the bills are up, or the command expires.
          await ack({ status: 'PROCESSING', result: { waitingForUnsentBills: sent.unsent } });
          return;
        }
        // The credential was captured above, so the completion can still be
        // reported after the wipe has cleared it from this device.
        const outcome = await wipeThisDevice();
        await ack({
          status: 'COMPLETED',
          result: { databaseErased: outcome.databaseErased, billsRefusedByServer: sent.rejected },
        });
        window.location.replace('/');
        return;
      }
      default:
        await ack({ status: 'FAILED', error: `This app version cannot run ${command.type}` });
    }
  } catch (error) {
    await ack({ status: 'FAILED', error: error instanceof Error ? error.message.slice(0, 200) : 'Failed' });
  }
}
